import React, { useEffect, useState } from 'react';
import { mspApi } from '../../services/api';
import './Msp.css';

function MspBranding() {
  const [branding, setBranding] = useState({
    logoUrl: '',
    primaryColor: '#00f3ff',
    vendorName: '',
    contactEmail: '',
    contactPhone: '',
  });
  const [status, setStatus] = useState('loading');
  const [saveStatus, setSaveStatus] = useState('idle');
  const [logoFile, setLogoFile] = useState(null);
  const [logoStatus, setLogoStatus] = useState('idle');
  const [brands, setBrands] = useState([]);
  const [selectedBrandId, setSelectedBrandId] = useState('');

  useEffect(() => {
    let isMounted = true;
    setStatus('loading');

    mspApi
      .getBranding()
      .then((response) => {
        if (!isMounted) return;
        const data = response.data || {};
        const availableBrands = data.brands || data.available_brands || [];
        setBrands(availableBrands);
        if (availableBrands.length > 0) {
          setSelectedBrandId(availableBrands[0].id || availableBrands[0].brand_id || '');
        }
        setBranding({
          logoUrl: data.logo_url || data.logoUrl || '',
          primaryColor: data.primary_color || data.primaryColor || '#00f3ff',
          vendorName: data.vendor_name || data.vendorName || '',
          contactEmail: data.contact_email || data.contactEmail || '',
          contactPhone: data.contact_phone || data.contactPhone || '',
        });
        setStatus('ready');
      })
      .catch(() => {
        if (isMounted) {
          setStatus('error');
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaveStatus('submitting');
    try {
      await mspApi.updateBranding({
        logo_url: branding.logoUrl,
        primary_color: branding.primaryColor,
        vendor_name: branding.vendorName,
        contact_email: branding.contactEmail,
        contact_phone: branding.contactPhone,
        brand_id: selectedBrandId || undefined,
      });
      setSaveStatus('success');
    } catch (error) {
      console.error('Failed to update branding', error);
      setSaveStatus('error');
    }
  };

  const handleLogoUpload = async () => {
    if (!logoFile) return;
    setLogoStatus('submitting');
    try {
      const formData = new FormData();
      formData.append('logo', logoFile);
      if (selectedBrandId) {
        formData.append('brand_id', selectedBrandId);
      }
      const response = await mspApi.uploadBrandLogo(formData);
      const logoUrl = response.data?.logo_url || response.data?.logoUrl;
      if (logoUrl) {
        setBranding((prev) => ({ ...prev, logoUrl }));
      }
      setLogoStatus('success');
    } catch (error) {
      console.error('Failed to upload logo', error);
      setLogoStatus('error');
    }
  };

  return (
    <div className="msp-page">
      <div className="msp-header">
        <div>
          <h1>Branding</h1>
          <p className="msp-subtitle">White-label settings for tenant reports</p>
        </div>
      </div>

      {status === 'loading' && <div className="msp-empty">Loading branding settings…</div>}
      {status === 'error' && <div className="msp-empty">Unable to load branding data.</div>}

      {status === 'ready' && (
        <>
          <form className="msp-form" onSubmit={handleSubmit}>
            {brands.length > 0 && (
              <label className="msp-form-field">
                Brand Profile
                <select
                  className="msp-input"
                  value={selectedBrandId}
                  onChange={(event) => setSelectedBrandId(event.target.value)}
                >
                  {brands.map((brand) => (
                    <option key={brand.id || brand.brand_id} value={brand.id || brand.brand_id}>
                      {brand.name || brand.vendor_name || 'Brand'}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label className="msp-form-field">
              Logo URL
              <input
                type="text"
                className="msp-input"
                value={branding.logoUrl}
                onChange={(event) => setBranding({ ...branding, logoUrl: event.target.value })}
              />
            </label>
            <label className="msp-form-field">
              Upload Logo
              <input
                type="file"
                className="msp-input"
                accept="image/*"
                onChange={(event) => setLogoFile(event.target.files?.[0] || null)}
              />
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleLogoUpload}
                disabled={logoStatus === 'submitting' || !logoFile}
              >
                {logoStatus === 'submitting' ? 'Uploading…' : 'Upload Logo'}
              </button>
            </label>
            <label className="msp-form-field">
              Primary Color
              <input
                type="color"
                className="msp-input msp-color-input"
                value={branding.primaryColor}
                onChange={(event) => setBranding({ ...branding, primaryColor: event.target.value })}
              />
            </label>
            <label className="msp-form-field">
              Vendor Name
              <input
                type="text"
                className="msp-input"
                value={branding.vendorName}
                onChange={(event) => setBranding({ ...branding, vendorName: event.target.value })}
              />
            </label>
            <label className="msp-form-field">
              Contact Email
              <input
                type="email"
                className="msp-input"
                value={branding.contactEmail}
                onChange={(event) => setBranding({ ...branding, contactEmail: event.target.value })}
              />
            </label>
            <label className="msp-form-field">
              Contact Phone
              <input
                type="text"
                className="msp-input"
                value={branding.contactPhone}
                onChange={(event) => setBranding({ ...branding, contactPhone: event.target.value })}
              />
            </label>
            <button type="submit" className="btn btn-primary" disabled={saveStatus === 'submitting'}>
              {saveStatus === 'submitting' ? 'Saving…' : 'Save Branding'}
            </button>
          </form>

          {saveStatus === 'error' && (
            <div className="msp-empty">Unable to save branding updates.</div>
          )}

          <div className="msp-report-preview">
            <h3>Report Header Preview</h3>
            <div className="msp-report-header" style={{ borderColor: branding.primaryColor }}>
              {branding.logoUrl && (
                <img src={branding.logoUrl} alt="Logo preview" className="msp-report-logo" />
              )}
              <div>
                <div className="msp-report-vendor" style={{ color: branding.primaryColor }}>
                  {branding.vendorName || 'Vendor Name'}
                </div>
                <div className="msp-report-contact">
                  {branding.contactEmail || 'contact@email.com'} · {branding.contactPhone || '555-000-0000'}
                </div>
              </div>
            </div>
            <div className="msp-report-template">
              <div className="msp-report-cover" style={{ borderColor: branding.primaryColor }}>
                <h4>Executive Security Report</h4>
                <p>Tenant Name · Month Year</p>
              </div>
              <div className="msp-report-summary">
                <h4>Summary Layout</h4>
                <p>
                  This template previews how the executive summary, key metrics, and next steps
                  will look with the selected branding profile.
                </p>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default MspBranding;
